import { Dispatch, SetStateAction } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { UserKeys } from '../../../../api/users-api';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { IP_Settings } from '../../../../../types';
import { Messages } from '../../../../utils/message-constants';
import { gPanelTiers } from '../../../../shared/gPanel-tiers';
import { useUserSettings } from '../../../../utils/use-user-settings';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Delete from '@mui/icons-material/Delete';
import Lock from '@mui/icons-material/Lock';
import LockPerson from '@mui/icons-material/LockPerson';
import Refresh from '@mui/icons-material/Refresh';
interface IpIntrusionToolbarProps {
  userRoles: string[];
  selectedRows: string[];
  setOpenIpAddress: Dispatch<SetStateAction<boolean>>;
  setDeleteIpIntrusionAddresses: Dispatch<SetStateAction<boolean>>;
  setOpenIpAddressToggleDialog: Dispatch<SetStateAction<boolean>>;
  setOpenApiIpAddressToggleDialog: Dispatch<SetStateAction<boolean>>;
  model: IP_Settings | undefined;
}

export default function IpIntrusionToolbar({
  userRoles,
  selectedRows,
  setOpenIpAddress,
  setDeleteIpIntrusionAddresses,
  setOpenIpAddressToggleDialog,
  setOpenApiIpAddressToggleDialog,
  model,
}: IpIntrusionToolbarProps) {
  const queryClient = useQueryClient();

  const { currentTier } = useUserSettings();

  const hasEnterprise = currentTier === 'enterprise';
  const hasAPIPermssion = isAbleToSeeUIElement(
    userRoles,
    AdministrationRoles.updateApiIpIntrusionStatus.value
  );

  return (
    <SharedToolbar noColumn noFilter>
      <Stack
        flexDirection="row"
        justifyContent="flex-end"
        alignItems="center"
        gap={1}
      >
        <Button
          variant="contained"
          onClick={() => setOpenIpAddress(true)}
          disabled={
            !isAbleToSeeUIElement(
              userRoles,
              AdministrationRoles.CreateIpIntrusionEntity.value
            )
          }
        >
          Add
        </Button>
        <Tooltip
          arrow
          title={
            model?.enabled ? 'Disable IP Intrusion' : 'Enable IP Intrusion'
          }
        >
          <IconButton
            onClick={() => setOpenIpAddressToggleDialog(true)}
            disabled={
              !isAbleToSeeUIElement(
                userRoles,
                AdministrationRoles.UpdateIpIntrusionStatus.value
              )
            }
          >
            <LockPerson color={model?.enabled ? 'primary' : 'inherit'} />
          </IconButton>
        </Tooltip>
        <Tooltip
          arrow
          title={
            !hasEnterprise
              ? Messages.Errors.Tier.Acting(gPanelTiers.enterprise.displayValue)
              : !hasAPIPermssion
              ? Messages.Errors.Permission.Acting(
                  AdministrationRoles.updateApiIpIntrusionStatus.name
                )
              : model?.apiIpRestricting
              ? 'Disable API IP Intrusion'
              : 'Enable API IP Intrusion'
          }
        >
          <span>
            <IconButton
              onClick={() => setOpenApiIpAddressToggleDialog(true)}
              disabled={!hasAPIPermssion || !hasEnterprise}
            >
              <Lock color={model?.apiIpRestricting ? 'primary' : 'inherit'} />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip arrow title="Delete">
          <span>
            <IconButton
              onClick={() => setDeleteIpIntrusionAddresses(true)}
              disabled={
                selectedRows.length === 0 ||
                !isAbleToSeeUIElement(
                  userRoles,
                  AdministrationRoles.DeleteIpIntrusionEntity.value
                )
              }
            >
              <Delete />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip arrow title="Refresh">
          <IconButton
            onClick={() => {
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.IpIntrusionSettings],
              });
            }}
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      </Stack>
    </SharedToolbar>
  );
}
